import type { FC } from 'react'
import { Paper, Group, Stack, Text, Badge, Button } from '@mantine/core'
import { IconChartDots3, IconSend } from '@tabler/icons-react'
import { DatasetType } from '@/lib/domain'
import type { AudienceConfiguration } from '@/lib/domain'
import { segmentKey } from '@/features/segment/api'
import { useSegmentStubs } from '@/features/segment/queries'
import type { SegmentStage } from '@/features/segment/components/SegmentDetail'
import { formatCompactNumber } from '@/lib/formatNumber'
import { matchColor } from '@/lib/matchColor'

const SHAPE_LABEL: Record<DatasetType, string> = {
    [DatasetType.TRANSACTION]: 'Transaction',
    [DatasetType.USER_ATTRIBUTE]: 'Attribute',
    [DatasetType.CROSSED]: 'Crossed',
}

export interface AudienceSegmentRowProps {
    config: AudienceConfiguration
    threadId: string
    onOpen: (config: AudienceConfiguration, stage: SegmentStage) => void
}

// One segment inside an audience's expansion. The model/distribute chips are
// clickable — they open the shared SegmentDetail at that stage (the same detail
// reused from the thread cart, "build once, two entry points").
export const AudienceSegmentRow: FC<AudienceSegmentRowProps> = ({
    config,
    threadId,
    onOpen,
}) => {
    const { lifecycleFor } = useSegmentStubs()
    const life = lifecycleFor(segmentKey(config), threadId)

    const modelLabel =
        life.modelStatus === 'ready'
            ? 'Modeled'
            : life.modelStatus === 'draft'
              ? 'Model draft'
              : 'Not modeled'
    const distLabel =
        life.distributeStatus === 'live'
            ? `Live · ${life.liveDestinationCount}`
            : life.distributeStatus === 'attention'
              ? 'Needs setup'
              : life.distributeStatus === 'pending'
                ? 'Scheduled'
                : 'Not sent'
    const distColor =
        life.distributeStatus === 'live'
            ? 'teal'
            : life.distributeStatus === 'attention'
              ? 'orange'
              : 'gray'

    return (
        <Paper withBorder p='sm' radius='md'>
            <Group justify='space-between' wrap='nowrap' align='center'>
                <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Group gap='xs' wrap='nowrap'>
                        <Text
                            fw={500}
                            size='sm'
                            truncate
                            style={{ minWidth: 0 }}
                        >
                            {config.name || 'Untitled segment'}
                        </Text>
                        <Badge size='xs' variant='light' color='gray'>
                            {SHAPE_LABEL[config.dataset_type]}
                        </Badge>
                    </Group>
                    <Text size='xs' c='dimmed'>
                        {formatCompactNumber(config.audience_size ?? 0)} users ·{' '}
                        <Text span c={matchColor(life.estimatedMatchPct)}>
                            {life.estimatedMatchPct}% est. match
                        </Text>
                    </Text>
                </Stack>
                <Group gap={6} wrap='nowrap'>
                    <Button
                        size='compact-xs'
                        variant='light'
                        color={life.modelStatus === 'ready' ? 'teal' : 'gray'}
                        leftSection={<IconChartDots3 size={13} />}
                        onClick={() => onOpen(config, 'model')}
                    >
                        {modelLabel}
                    </Button>
                    <Button
                        size='compact-xs'
                        variant='light'
                        color={distColor}
                        leftSection={<IconSend size={13} />}
                        onClick={() => onOpen(config, 'distribute')}
                    >
                        {distLabel}
                    </Button>
                    <Button
                        size='compact-xs'
                        variant='subtle'
                        onClick={() => onOpen(config, 'makeup')}
                    >
                        Details
                    </Button>
                </Group>
            </Group>
        </Paper>
    )
}
